import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Icon } from 'core/generic/elements/icons';

import { NavItemType } from '../../types';
import { MobileNav } from '../mobile-nav/mobile-nav';
import { NavItem } from '../nav-item/nav-item';
import styles from './nav.module.scss';

export type NavProps = {
	items: NavItemType[];
};

export const Nav = ({ items }: NavProps) => {
	const { i18n, t } = useTranslation();
	const isRTL = i18n?.dir ? i18n.dir() === 'rtl' : false;
	const [showMobileNav, setShowMobileNav] = useState(false);
	const [hiddenItems, setHiddenItems] = useState<number[]>([]);
	const [activeSubNavIndex, setActiveSubNavIndex] = useState(-1);

	useEffect(() => {
		if (!showMobileNav) {
			document.body.classList.remove('no-scroll');
			return;
		}

		const handleResize = () => {
			if (window.innerWidth >= 600 && showMobileNav)
				setShowMobileNav(false);
		};

		document.body.classList.add('no-scroll');
		window.addEventListener('resize', handleResize);

		return () => window.removeEventListener('resize', handleResize);
	}, [showMobileNav]);

	const navRef = useRef<HTMLUListElement>(null);

	const sortMoreItems = hiddenItems
		.sort((a: number, b: number) => a - b)
		.map((index: number) => items[index]);

	const handleMobileNav = (isOpen: boolean) => {
		setShowMobileNav(isOpen);
	};

	const handleSubNavMouseEnter = (index: number) => {
		setActiveSubNavIndex(index);
	};

	const handleSubNavMouseLeave = () => {
		setActiveSubNavIndex(-1);
	};

	const handleToggleMobileNav = () => {
		setShowMobileNav((state) => !state);
	};

	const intersectingCallback = useCallback(
		(
			entries: IntersectionObserverEntry[],
			children: NodeListOf<Element>
		) => {
			entries.forEach((entry: IntersectionObserverEntry) => {
				const index = Array.from(children).indexOf(entry.target);

				if (entry.isIntersecting) {
					setHiddenItems((prevItems) => {
						return prevItems.filter((item) => item !== index);
					});

					entry.target.classList.remove(styles.hidden);
				} else {
					setHiddenItems((prevItems) => {
						return !prevItems.includes(index)
							? [...prevItems, index]
							: prevItems;
					});

					entry.target.classList.add(styles.hidden);
				}
			});
		},
		[]
	);

	useEffect(() => {
		if (
			typeof window === 'undefined' ||
			typeof window.ResizeObserver === 'undefined'
		) {
			return;
		}
		const navContainer = navRef.current;
		if (!navContainer) return;

		let timeoutId: ReturnType<typeof setTimeout> | undefined;

		const resizeObserver = new ResizeObserver(() => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}

			timeoutId = setTimeout(() => {
				setHiddenItems([]);
			}, 100);
		});

		resizeObserver.observe(navContainer);

		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
			resizeObserver.disconnect();
		};
	}, [setHiddenItems]);

	useEffect(() => {
		const navContainer = navRef.current;
		if (!navContainer) return;

		let observer: IntersectionObserver | null = null;
		let animationFrameId: number;

		const setupObserver = () => {
			animationFrameId = requestAnimationFrame(() => {
				if (observer) observer.disconnect();

				const children = navContainer.querySelectorAll<HTMLElement>(
					`:scope > li:not(.more)`
				);

				const hasOverflow =
					navContainer.scrollWidth > navContainer.clientWidth;
				const rightMargin = hasOverflow ? -80 : 10;
				const leftMargin = 10;

				observer = new IntersectionObserver(
					(entries) => intersectingCallback(entries, children),
					{
						root: navContainer,
						rootMargin: isRTL
							? `4000px ${leftMargin}px 4000px ${rightMargin}px`
							: `4000px ${rightMargin}px 4000px ${leftMargin}px`,
						threshold: 1.0,
					}
				);

				children.forEach((child) => observer?.observe(child));
			});
		};

		setupObserver();

		return () => {
			cancelAnimationFrame(animationFrameId);
			if (observer) observer.disconnect();
		};
	}, [items.length, hiddenItems.length === 0, intersectingCallback, isRTL]);

	return (
		<>
			<nav
				className={styles.nav}
				role="navigation"
				aria-label="Primary Navigation"
			>
				{items && (
					<ul className={styles['nav-list']} ref={navRef}>
						{items.map((item, index) => {
							return (
								<NavItem
									item={item}
									key={index}
									endItem={false}
									startItem={index === 0}
									customStyles={''}
									isActive={activeSubNavIndex === index}
									onMouseEnter={() =>
										handleSubNavMouseEnter(index)
									}
									onMouseLeave={handleSubNavMouseLeave}
								/>
							);
						})}

						{sortMoreItems.length > 0 && (
							<NavItem
								item={{
									key: 'more',
									title: t(
										'global.header:more_items_button',
										'More'
									),
									url: '#',
									items: sortMoreItems,
								}}
								customStyles={`${styles.more} more`}
								endItem={true}
								isActive={activeSubNavIndex === 99}
								onMouseEnter={() => handleSubNavMouseEnter(99)}
								onMouseLeave={() => handleSubNavMouseLeave()}
							/>
						)}
					</ul>
				)}
			</nav>

			<button
				onClick={handleToggleMobileNav}
				className={`${styles.hamburger}`}
				aria-label="Open navigation"
			>
				<Icon variant="Menu" />
			</button>

			<MobileNav
				items={items}
				isOpen={showMobileNav}
				callback={handleMobileNav}
				direction={isRTL ? 'Right' : 'Left'}
				primary={true}
			/>
		</>
	);
};
